import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiRefreshCw, FiTarget } from 'react-icons/fi';
import {
  FaClock,
  FaCrosshairs,
  FaMoon,
  FaTachometerAlt,
} from 'react-icons/fa';
import { YButton } from '../../../ui/YButton';
import api from '../../../utils/api';
import { getCachedTopPicks, fetchTopPicks } from '../../../utils/forecastCache';
import ForecastEightDayChart from './ForecastEightDayChart';
import ForecastScoreRing from './ForecastScoreRing';
import ForecastStandDetailDialog from './ForecastStandDetailDialog';
import ForecastTagOutDialog from './ForecastTagOutDialog';
import ForecastTrackLoader from './ForecastTrackLoader';
import {
  buildHeroWhy,
  formatMajorWindow,
  formatMinorWindow,
  formatPressureInHg,
  formatSkyLabel,
  formatSummaryDate,
  getForecastScoreBandClass,
  getForecastScoreLabel,
  getHeroFactorBars,
  getPersonalStandMapPath,
  isWindOptimal,
  toForecastCardinal,
} from '../huntForecastUtils';
import './hunt-forecast-shell.css';

function StandPicker({ source, value, onChange, clubOptions, personalOptions, loading, error }) {
  const options = source === 'club' ? clubOptions : personalOptions;
  return (
    <div className="hf-stand-picker">
      <label className="hf-stand-picker__label" htmlFor="hf-stand-select">
        Choose stand
      </label>
      <select
        id="hf-stand-select"
        className="form-select hf-stand-picker__select"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        disabled={loading}
        data-testid="hf-stand-select"
      >
        <option value="">{loading ? 'Loading…' : 'Select a stand…'}</option>
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>{opt.name}</option>
        ))}
      </select>
      {error ? <div className="text-danger small mt-1">{error}</div> : null}
    </div>
  );
}

function RankedStandCard({
  pick,
  rank,
  onOpen,
  onTagOut,
  onOpenMap,
  altRow,
}) {
  const score = pick?.day?.score;
  const { label } = getForecastScoreLabel(score);
  const weather = pick?.day?.weather || {};
  const windCardinal = weather.wind_cardinal || toForecastCardinal(weather.wind_deg);
  const windSpeed = typeof weather.wind_speed === 'number' ? Math.round(weather.wind_speed) : null;
  const windLabel = windCardinal && windSpeed != null
    ? `${windCardinal} ${windSpeed}`
    : '—';
  const windOk = isWindOptimal(pick);
  const isClub = pick.kind === 'club';

  return (
    <div
      className={`hf-ranked__row${altRow ? ' is-alt' : ''}`}
      data-testid={`top-pick-card-${pick.kind}-${pick.id}`}
    >
      <span className="hf-ranked__rank" aria-hidden="true">{rank}</span>
      <button
        type="button"
        className="hf-ranked__main border-0 bg-transparent p-0 text-start"
        onClick={() => onOpen(pick)}
        aria-label={`Open forecast for ${pick.name}`}
      >
        <ForecastScoreRing
          score={score}
          size={36}
          showLabel={false}
          className="hf-score-ring--enamel hf-score-ring--ranked"
        />
        <span className="hf-ranked__copy">
          <span className="hf-ranked__name">{pick.name}</span>
          <span className="hf-ranked__meta">
            {windLabel}
            {' · MAJOR '}
            {formatMajorWindow(pick)}
          </span>
        </span>
      </button>
      <span
        className={`hf-band-pill hf-band-pill--ink ${getForecastScoreBandClass(score)}`}
      >
        {label}
      </span>
      {windOk ? (
        <span className="hf-ranked__wind-ok">✓ Wind</span>
      ) : null}
      <button
        type="button"
        className="hf-ranked__detail"
        onClick={() => onOpen(pick)}
      >
        Detail
      </button>
      {isClub ? (
        <button
          type="button"
          className="hf-ranked__action"
          onClick={() => onTagOut(pick)}
        >
          Tag Out
        </button>
      ) : (
        <button
          type="button"
          className="hf-ranked__action"
          onClick={() => onOpenMap(pick)}
        >
          Map
        </button>
      )}
    </div>
  );
}

export default function HuntForecastPage({ embedded = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [source, setSource] = useState('club');
  const [topPicks, setTopPicks] = useState({
    loading: true,
    refreshing: false,
    hasLoaded: false,
    error: null,
    picks: [],
  });
  const [clubStands, setClubStands] = useState({ options: [], loading: true, error: '' });
  const [personalStands, setPersonalStands] = useState({ options: [], loading: true, error: '' });
  const [selectedStandId, setSelectedStandId] = useState(null);
  const [detailPick, setDetailPick] = useState(null);
  const [tagOutPick, setTagOutPick] = useState(null);
  const [clubMetaById, setClubMetaById] = useState({});

  const loadTopPicks = useCallback(async (activeSource, useCacheFirst = true) => {
    const filterPicks = (data) => (Array.isArray(data?.picks) ? data.picks : [])
      .filter((p) => p.kind === activeSource);

    const cached = useCacheFirst ? getCachedTopPicks({ source: activeSource, limit: 6 }) : null;
    const cachedFiltered = cached?.data ? filterPicks(cached.data) : [];

    if (cached?.data?.picks && useCacheFirst && cachedFiltered.length > 0) {
      setTopPicks({
        loading: false,
        refreshing: true,
        hasLoaded: true,
        error: null,
        picks: cachedFiltered,
      });
      try {
        const resp = await fetchTopPicks(api, { source: activeSource, limit: 6 });
        const picks = filterPicks(resp.data);
        setTopPicks({
          loading: false,
          refreshing: false,
          hasLoaded: true,
          error: null,
          picks,
        });
      } catch {
        setTopPicks((prev) => ({ ...prev, refreshing: false }));
      }
      return;
    }

    setTopPicks({
      loading: true,
      refreshing: false,
      hasLoaded: false,
      error: null,
      picks: cachedFiltered.length > 0 ? cachedFiltered : [],
    });
    try {
      const resp = await fetchTopPicks(api, { source: activeSource, limit: 6 });
      const picks = filterPicks(resp.data);
      setTopPicks({
        loading: false,
        refreshing: false,
        hasLoaded: true,
        error: null,
        picks,
      });
    } catch {
      setTopPicks({
        loading: false,
        refreshing: false,
        hasLoaded: true,
        error: 'Unable to load ranked forecasts right now.',
        picks: [],
      });
    }
  }, []);

  useEffect(() => {
    loadTopPicks(source).catch(() => {});
  }, [source, loadTopPicks]);

  useEffect(() => {
    let cancelled = false;
    setClubStands((prev) => ({ ...prev, loading: true, error: '' }));
    api.get('/hunt-locations/')
      .then((resp) => {
        if (cancelled) return;
        const items = Array.isArray(resp?.data?.results) ? resp.data.results : (resp?.data || []);
        setClubStands({
          options: (items || []).map((it) => ({ id: it.id, name: it.name || 'Club stand' })),
          loading: false,
          error: '',
        });
      })
      .catch(() => {
        if (cancelled) return;
        setClubStands({ options: [], loading: false, error: 'Unable to load club stands.' });
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPersonalStands((prev) => ({ ...prev, loading: true, error: '' }));
    api.get('/features/')
      .then((resp) => {
        if (cancelled) return;
        const items = Array.isArray(resp?.data) ? resp.data : [];
        const filtered = items.filter((feature) => {
          const type = (feature?.geojson?.type || feature?.type || '').toLowerCase();
          if (type !== 'point') return false;
          const markerType = feature?.marker_type || feature?.markerType;
          return !markerType;
        });
        setPersonalStands({
          options: filtered.map((feature) => ({
            id: feature.id,
            name: feature.name || 'Personal stand',
          })),
          loading: false,
          error: '',
        });
      })
      .catch(() => {
        if (cancelled) return;
        setPersonalStands({ options: [], loading: false, error: 'Unable to load personal stands.' });
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams(location.search);
        const standId = params.get('standId');
        if (standId) {
          const parsed = Number(standId);
          if (!Number.isNaN(parsed) && !cancelled) {
            setSource('club');
            setSelectedStandId(parsed);
          }
          return;
        }
        const prof = await api.get('/profiles/me/');
        const primaryId = prof?.data?.primary_hunt_location_id || null;
        if (!cancelled && primaryId) setSelectedStandId(primaryId);
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [location.search]);

  useEffect(() => {
    if (source !== 'club') return undefined;
    let cancelled = false;
    const ids = topPicks.picks.filter((p) => p.kind === 'club').map((p) => p.id);
    ids.forEach((id) => {
      api.get(`/hunt-locations/${id}/`).then((resp) => {
        if (cancelled) return;
        setClubMetaById((prev) => {
          if (prev[id] !== undefined) return prev;
          return { ...prev, [id]: { clubId: resp?.data?.club_id || null } };
        });
      }).catch(() => {
        if (cancelled) return;
        setClubMetaById((prev) => {
          if (prev[id] !== undefined) return prev;
          return { ...prev, [id]: { clubId: null } };
        });
      });
    });
    return () => { cancelled = true; };
  }, [source, topPicks.picks]);

  const ranked = useMemo(
    () => [...topPicks.picks].sort((a, b) => (b?.day?.score ?? -1) - (a?.day?.score ?? -1)),
    [topPicks.picks],
  );

  const topPick = ranked[0] || null;
  const rest = ranked.slice(1);
  const moonPhase = topPick?.day?.lunar_phase || ranked.find((p) => p.day?.lunar_phase)?.day?.lunar_phase;

  const findPickById = useCallback((id) => {
    if (!id) return null;
    const fromRanked = ranked.find((p) => p.id === id);
    if (fromRanked) return fromRanked;
    const options = source === 'club' ? clubStands.options : personalStands.options;
    const opt = options.find((o) => o.id === id);
    if (!opt) return null;
    return {
      id: opt.id,
      kind: source === 'personal' ? 'personal' : 'club',
      name: opt.name,
      club: null,
      day: null,
    };
  }, [ranked, source, clubStands.options, personalStands.options]);

  const handleStandSelect = (id) => {
    setSelectedStandId(id);
    if (!id) return;
    const pick = findPickById(id);
    if (pick) setDetailPick(pick);
  };

  const handleOpenDetail = (pick) => {
    setDetailPick(pick);
    setSelectedStandId(pick?.id ?? null);
  };

  const handleTagOutConfirm = (pick) => {
    setTagOutPick(null);
    setDetailPick(null);
    const clubId = pick?.club?.id ?? clubMetaById[pick.id]?.clubId;
    if (clubId && pick?.id) {
      navigate(`/clubs/${clubId}/map?standId=${pick.id}`);
    }
  };

  const handleOpenMap = (pick) => {
    setDetailPick(null);
    if (pick?.kind === 'club') {
      const clubId = pick?.club?.id ?? clubMetaById[pick.id]?.clubId;
      if (clubId && pick?.id) {
        navigate(`/clubs/${clubId}/map?standId=${pick.id}`);
        return;
      }
    }
    navigate(getPersonalStandMapPath(pick?.id));
  };

  const heroWeather = topPick?.day?.weather || {};
  const heroTemp = heroWeather.temp_max != null ? Math.round(heroWeather.temp_max) : null;
  const heroWind = typeof heroWeather.wind_speed === 'number' ? Math.round(heroWeather.wind_speed) : null;
  const heroWindCardinal = heroWeather.wind_cardinal || toForecastCardinal(heroWeather.wind_deg);
  const heroPressure = formatPressureInHg(heroWeather.pressure);
  const heroScore = topPick?.day?.score;
  const heroBand = getForecastScoreLabel(heroScore);
  const heroFactors = getHeroFactorBars(topPick?.day);
  const heroWhy = buildHeroWhy(topPick?.day);
  const heroGround = topPick?.kind === 'club'
    ? `${topPick?.club?.name || 'Club'} · Club stand`
    : 'My Ground · Personal stand';
  const heroHasFactors = heroFactors.some((f) => f.value != null);

  const standsLoading = source === 'club' ? clubStands.loading : personalStands.loading;
  const forecastPending = topPicks.loading || topPicks.refreshing;
  const showForecastLoader = ranked.length === 0 && (
    forecastPending || !topPicks.hasLoaded || standsLoading
  );
  const showEmptyState = topPicks.hasLoaded && !forecastPending && !standsLoading && ranked.length === 0;

  const setForecastSource = (next) => {
    setSource(next);
    setSelectedStandId(null);
  };

  return (
    <div
      className={`hf-shell${embedded ? ' hf-shell--embedded' : ''}`}
      data-testid="hunt-forecast-page"
    >
      <header className="hf-header">
        <div className="hf-header__top">
          <div className="hf-header__brand">
            <div className="hf-header__icon" aria-hidden="true">
              <FiTarget size={16} />
            </div>
            <div>
              {embedded ? (
                <span className="hf-header__title">Hunt Forecast</span>
              ) : (
                <h1 className="hf-header__title">Hunt Forecast</h1>
              )}
              <p className="hf-header__subtitle">Stand conditions &amp; feeding windows</p>
            </div>
          </div>
          <div
            className="hf-source-seg"
            role="group"
            aria-label="Forecast source"
            data-testid="hf-source-select"
          >
            <button
              type="button"
              className={`hf-source-seg__btn${source === 'personal' ? ' is-active' : ''}`}
              aria-pressed={source === 'personal'}
              data-testid="hf-source-personal"
              onClick={() => setForecastSource('personal')}
            >
              My Stands
            </button>
            <button
              type="button"
              className={`hf-source-seg__btn${source === 'club' ? ' is-active' : ''}`}
              aria-pressed={source === 'club'}
              data-testid="hf-source-club"
              onClick={() => setForecastSource('club')}
            >
              Club Stands
            </button>
          </div>
        </div>
      </header>

      <div className="hf-body">
        <div className="hf-summary" data-testid="hf-summary">
          <div className="hf-summary__item">
            <FaClock aria-hidden="true" />
            <span>
              Today ·
              {' '}
              {formatSummaryDate()}
            </span>
          </div>
          {moonPhase ? (
            <div className="hf-summary__item">
              <FaMoon aria-hidden="true" />
              <span>{moonPhase}</span>
            </div>
          ) : null}
          <div className="hf-summary__item">
            <FaTachometerAlt aria-hidden="true" />
            <span>
              {showForecastLoader
                ? 'Ranking stands…'
                : `${ranked.length} stands ranked`}
            </span>
          </div>
        </div>

        <StandPicker
          source={source}
          value={selectedStandId}
          onChange={handleStandSelect}
          clubOptions={clubStands.options}
          personalOptions={personalStands.options}
          loading={source === 'club' ? clubStands.loading : personalStands.loading}
          error={source === 'club' ? clubStands.error : personalStands.error}
        />

        {showForecastLoader ? (
          <ForecastTrackLoader />
        ) : topPicks.error ? (
          <div className="hf-error">
            <div>{topPicks.error}</div>
            <YButton variant="secondary" size="sm" className="mt-2" onClick={() => loadTopPicks(source, false)}>
              <FiRefreshCw aria-hidden="true" />
              Try again
            </YButton>
          </div>
        ) : showEmptyState ? (
          <div className="hf-empty" data-testid="hf-empty-state">
            No stand data yet. Add stands on your map to start ranking them.
          </div>
        ) : (
          <>
            {topPick ? (
              <div
                className="hf-hero"
                data-testid={`top-pick-card-${topPick.kind}-${topPick.id}`}
              >
                <div className="hf-hero__head">
                  <span className="hf-hero__best-bet">
                    Best Bet ·
                    {' '}
                    {topPick.name}
                  </span>
                  <span
                    className={`hf-band-pill ${getForecastScoreBandClass(heroScore)}`}
                    data-testid="hf-hero-band"
                  >
                    {heroBand.label}
                  </span>
                </div>
                <div className="hf-hero__grid">
                  <div className="hf-hero__main">
                    <button
                      type="button"
                      className="hf-hero__identity border-0 bg-transparent p-0 text-start w-100"
                      onClick={() => handleOpenDetail(topPick)}
                      aria-label={`Open forecast for ${topPick.name}`}
                    >
                      <ForecastScoreRing
                        score={heroScore}
                        size={96}
                        showLabel={false}
                        className="hf-score-ring--enamel"
                      />
                      <div className="hf-hero__copy">
                        <h3 className="hf-hero__name">{topPick.name}</h3>
                        <div className="hf-hero__ground">{heroGround}</div>
                        <p className="hf-hero__why">{heroWhy}</p>
                      </div>
                    </button>
                    {heroHasFactors ? (
                      <div className="hf-hero__factors" data-testid="hf-hero-factors">
                        {heroFactors.map((f) => (
                          <div key={f.key} className="hf-hero__factor">
                            <span className="hf-hero__factor-label">{f.label}</span>
                            <span className="hf-hero__factor-track" aria-hidden="true">
                              <span
                                className={`hf-hero__factor-fill hf-hero__factor-fill--${f.key}`}
                                style={{ width: `${f.width}%` }}
                              />
                            </span>
                            <span className="hf-hero__factor-value">
                              {f.value == null ? '—' : f.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className="hf-hero__aside">
                    <div className="hf-hero__wx" data-testid="hf-hero-wx">
                      <div className="hf-hero__wx-cell">
                        <div className="hf-hero__wx-label">Temp</div>
                        <div className="hf-hero__wx-value">
                          {heroTemp != null ? `${heroTemp}°` : '—'}
                        </div>
                      </div>
                      <div className="hf-hero__wx-cell">
                        <div className="hf-hero__wx-label">Wind</div>
                        <div className="hf-hero__wx-value">
                          {heroWindCardinal && heroWind != null
                            ? `${heroWindCardinal} ${heroWind}`
                            : '—'}
                        </div>
                      </div>
                      <div className="hf-hero__wx-cell">
                        <div className="hf-hero__wx-label">Pressure</div>
                        <div className="hf-hero__wx-value">{heroPressure || '—'}</div>
                      </div>
                      <div className="hf-hero__wx-cell">
                        <div className="hf-hero__wx-label">Sky</div>
                        <div className="hf-hero__wx-value">
                          {formatSkyLabel(heroWeather.weather_type)}
                        </div>
                      </div>
                    </div>
                    <div className="hf-window-box">
                      <div className="hf-window-box__label">Next major window</div>
                      <div className="hf-window-box__value">{formatMajorWindow(topPick)}</div>
                      <div className="hf-window-box__minor">
                        Minor
                        {' '}
                        {formatMinorWindow(topPick)}
                      </div>
                    </div>
                    <div className="hf-hero__actions">
                      {topPick.kind === 'club' ? (
                        <YButton
                          variant="primary"
                          className="hf-hero__cta-primary"
                          data-testid="hf-hero-tag-out"
                          onClick={() => setTagOutPick(topPick)}
                        >
                          <FaCrosshairs aria-hidden="true" />
                          Tag Out to Hunt
                        </YButton>
                      ) : null}
                      <YButton
                        variant={topPick.kind === 'club' ? 'secondary' : 'primary'}
                        className="hf-hero__cta-map"
                        data-testid="hf-hero-open-map"
                        onClick={() => handleOpenMap(topPick)}
                      >
                        Open on Map
                      </YButton>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {topPick ? <ForecastEightDayChart pick={topPick} /> : null}

            {rest.length > 0 && (
              <section className="hf-ranked" data-testid="hf-ranked" aria-label="Ranked forecast">
                <div className="hf-ranked__head">Ranked Forecast</div>
                <div className="hf-ranked__list">
                  {rest.map((pick, idx) => (
                    <RankedStandCard
                      key={`${pick.kind}-${pick.id}`}
                      pick={pick}
                      rank={idx + 2}
                      altRow={idx % 2 === 1}
                      onOpen={handleOpenDetail}
                      onTagOut={setTagOutPick}
                      onOpenMap={handleOpenMap}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      <ForecastStandDetailDialog
        show={Boolean(detailPick)}
        onHide={() => setDetailPick(null)}
        pick={detailPick}
        onTagOut={setTagOutPick}
        onOpenMap={handleOpenMap}
      />

      <ForecastTagOutDialog
        show={Boolean(tagOutPick)}
        onHide={() => setTagOutPick(null)}
        pick={tagOutPick}
        onConfirm={handleTagOutConfirm}
      />
    </div>
  );
}
